/**
 * WhatsAppButton — Botón con menú de plantillas.
 *
 * Click → menú con plantillas activas (filtradas por aplica_a).
 * Selección → abre wa.me/<numero>?text=<plantilla_renderizada> en nueva pestaña.
 *
 * Variables sustituibles: {nombre_contacto}, {comercial_nombre}, {empresa}.
 */
import React, { useEffect, useState } from 'react';
import {
  Button, Menu, MenuItem, Box, Typography, CircularProgress,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { API_BASE, getEmpresaId, getAuthHeaders, getCurrentUser } from './format';

function renderizar(template, vars) {
  let out = template || '';
  for (const [k, v] of Object.entries(vars)) {
    out = out.split(`{${k}}`).join(v || '');
  }
  return out.replace(/\{[a-z_][a-z0-9_]*\}/g, '');
}

function normalizarNumero(s) {
  if (!s) return '';
  const limpio = String(s).replace(/[^0-9+]/g, '');
  return limpio.startsWith('+') ? limpio.slice(1) : limpio;
}

export default function WhatsAppButton({
  numero,
  nombreContacto,
  empresaNombre,
  aplicaA = 'ambos',  // 'agencia' | 'prospecto' | 'ambos'
  size = 'small',
  variant = 'contained',
}) {
  const me = getCurrentUser();
  const [anchorEl, setAnchorEl] = useState(null);
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(false);

  const cargarPlantillas = async () => {
    if (plantillas.length > 0) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        empresa_id: getEmpresaId(),
        solo_activas: 'true',
        aplica_a: aplicaA,
      });
      const r = await fetch(`${API_BASE}/turismo/comercial/plantillas-whatsapp?${params}`,
        { headers: getAuthHeaders() });
      const d = await r.json();
      setPlantillas(d.plantillas || []);
    } catch { setPlantillas([]); }
    setLoading(false);
  };

  const handleOpen = async (e) => {
    setAnchorEl(e.currentTarget);
    await cargarPlantillas();
  };

  const handleClose = () => setAnchorEl(null);

  const handleSeleccionar = (plantilla) => {
    const numNormalizado = normalizarNumero(numero);
    if (!numNormalizado) {
      alert('Este contacto no tiene número de WhatsApp registrado.');
      handleClose();
      return;
    }
    const texto = renderizar(plantilla.cuerpo, {
      nombre_contacto: nombreContacto || 'amigo/a',
      comercial_nombre: me.nombre || me.email?.split('@')[0] || 'el equipo comercial',
      empresa: empresaNombre || 'Entretur',
    });
    const url = `https://wa.me/${numNormalizado}?text=${encodeURIComponent(texto)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  const handleVacio = () => {
    const numNormalizado = normalizarNumero(numero);
    if (!numNormalizado) {
      alert('Este contacto no tiene número de WhatsApp registrado.');
      handleClose();
      return;
    }
    window.open(`https://wa.me/${numNormalizado}`, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  const deshabilitado = !numero;

  return (
    <>
      <Button
        variant={variant}
        size={size}
        startIcon={<WhatsAppIcon />}
        onClick={handleOpen}
        disabled={deshabilitado}
        disableElevation
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          ...(variant === 'contained' && {
            background: '#25D366',
            color: '#fff',
            '&:hover': { background: '#128C7E' },
          }),
          ...(variant === 'outlined' && {
            borderColor: '#25D366',
            color: '#25D366',
            '&:hover': { borderColor: '#128C7E', bgcolor: '#25D36610' },
          }),
        }}>
        WhatsApp
      </Button>

      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}
        PaperProps={{ sx: { minWidth: 280, maxWidth: 360, borderRadius: 2 } }}>
        {loading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <CircularProgress size={20} sx={{ color: '#25D366' }} />
          </Box>
        ) : (
          [
            <Box key="header" sx={{ px: 1.5, py: 0.5,
                                      borderBottom: '1px solid #f1f5f9', mb: 0.5 }}>
              <Typography sx={{ fontSize: 10, color: '#64748b',
                                  textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
                Plantilla rápida
              </Typography>
            </Box>,
            ...plantillas.map(p => (
              <MenuItem key={p.id} onClick={() => handleSeleccionar(p)}
                sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{p.nombre}</Typography>
                {p.descripcion && (
                  <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>
                    {p.descripcion}
                  </Typography>
                )}
              </MenuItem>
            )),
            <Box key="divider" sx={{ borderTop: '1px solid #f1f5f9', mt: 0.5 }} />,
            <MenuItem key="empty" onClick={handleVacio} sx={{ py: 1 }}>
              <Typography sx={{ fontSize: 12.5, color: '#475569' }}>
                Abrir sin mensaje pre-redactado
              </Typography>
            </MenuItem>,
          ]
        )}
      </Menu>
    </>
  );
}
